import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChatBubbleLeftEllipsisIcon,
  TrashIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  DevicePhoneMobileIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import EmptyState from '../ui/EmptyState';
import ViewState from '../ui/ViewState';
import PageHeader from '../ui/PageHeader';
import TableToolbar from '../ui/TableToolbar';
import Button from '../ui/Button';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import Table, { TableHead, TableBody, TableRow, TableHeaderCell, TableCell } from '../ui/Table';
import {
  openSingleContactSms,
  isMobileDevice,
  tryPickContactsFromDevice,
} from '../../utils/contacts';
import { getContactImportOptions } from '../../utils/contacts';
import { parseVCardFile } from '../../utils/contacts';
import { copyTextToClipboard } from '../../utils/clipboard';
import CopyablePhone from '../ui/CopyablePhone';
import { mapDualProgramLinks, referralUrlForLink } from '../../utils/referralLinks';

function GoogleIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function ClientContactsView() {
  const { t, formatMessage } = useLanguage();
  const { confirm, dialog } = useConfirmDialog();
  const c = t.myContacts;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [invitingId, setInvitingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [importMode, setImportMode] = useState(null);
  const vcardInputRef = useRef(null);

  const importOptions = useMemo(() => getContactImportOptions(), []);

  const loadInviteMessage = useCallback(async () => {
    try {
      const response = await axios.get('/api/referral-links', { withCredentials: true });
      const links = response.data || [];
      const { clientsLink } = mapDualProgramLinks(links);
      const url = referralUrlForLink(clientsLink);
      if (url) {
        setInviteMessage(formatMessage(t.enrola.defaultShareClients, { url }));
      }
    } catch (error) {
      console.error('Failed to load referral link for contacts:', error);
    }
  }, [formatMessage, t.enrola.defaultShareClients]);

  const fetchContacts = useCallback(async (query = '') => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = query.trim() ? { q: query.trim() } : undefined;
      const response = await axios.get('/api/client/contacts', {
        params,
        withCredentials: true,
      });
      setContacts(response.data?.contacts || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
      setLoadError(c.failedToLoad);
      toast.error(c.failedToLoad);
    } finally {
      setLoading(false);
    }
  }, [c.failedToLoad]);

  const loadGoogleStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/client/contacts/google/status', {
        withCredentials: true,
      });
      setGoogleConfigured(Boolean(response.data?.configured));
    } catch (error) {
      setGoogleConfigured(false);
    }
  }, []);

  useEffect(() => {
    loadInviteMessage();
    loadGoogleStatus();
  }, [loadInviteMessage, loadGoogleStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchContacts(search);
    }, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [search, fetchContacts]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const importResult = params.get('google_import');
    if (!importResult) return;

    if (importResult === 'success') {
      const created = params.get('created') || '0';
      const updated = params.get('updated') || '0';
      toast.success(formatMessage(c.googleImportSuccess, { created, updated }));
      fetchContacts(search);
    } else {
      const reason = params.get('reason');
      if (reason === 'access_denied') {
        toast.error(c.googleImportCancelled);
      } else if (reason === 'no_contacts_with_phone') {
        toast.error(c.googleImportNoPhoneContacts);
      } else {
        toast.error(c.googleImportFailed);
      }
    }

    window.history.replaceState({}, '', '/dashboard?view=contacts');
  }, [c.googleImportCancelled, c.googleImportFailed, c.googleImportNoPhoneContacts, c.googleImportSuccess, fetchContacts, formatMessage, search]);

  const invitedCount = useMemo(
    () => contacts.filter((row) => row.invited_at).length,
    [contacts]
  );

  const platformHint = useMemo(() => {
    if (importOptions.platform === 'ios') return c.iosImportHint;
    if (importOptions.platform === 'android') return c.androidImportHint;
    return c.desktopImportHint;
  }, [c.androidImportHint, c.desktopImportHint, c.iosImportHint, importOptions.platform]);

  const submitImportedContacts = useCallback(
    async (rows, source) => {
      if (!rows.length) {
        toast.error(c.importEmpty);
        return false;
      }

      const response = await axios.post(
        '/api/client/contacts/import',
        { contacts: rows, source },
        { withCredentials: true }
      );

      setContacts(response.data?.contacts || []);
      const created = response.data?.created || 0;
      const updated = response.data?.updated || 0;
      toast.success(formatMessage(c.importSuccess, { created, updated }));
      return true;
    },
    [c.importEmpty, c.importSuccess, formatMessage]
  );

  const handleGoogleImport = async () => {
    if (importing) return;

    setImporting(true);
    setImportMode('google');
    try {
      const response = await axios.get('/api/client/contacts/oauth/google/start', {
        withCredentials: true,
      });
      const authorizationUrl = response.data?.authorization_url;
      if (!authorizationUrl) {
        toast.error(c.googleImportFailed);
        return;
      }
      window.location.href = authorizationUrl;
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error(c.googleNotConfigured);
      } else {
        toast.error(error.response?.data?.error || c.googleImportFailed);
      }
      setImporting(false);
      setImportMode(null);
    }
  };

  const handlePhonePickerImport = async () => {
    if (importing) return;

    setImporting(true);
    setImportMode('phone');
    try {
      const picked = await tryPickContactsFromDevice({ multiple: true });
      await submitImportedContacts(picked, 'phone_import');
    } catch (error) {
      if (error?.message === 'CANCELLED') return;
      if (error?.message === 'UNSUPPORTED') {
        toast.error(c.phonePickerUnsupported);
        return;
      }
      toast.error(error.response?.data?.error || c.importFailed);
    } finally {
      setImporting(false);
      setImportMode(null);
    }
  };

  const handleVCardImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || importing) return;

    setImporting(true);
    setImportMode('vcard');
    try {
      const rows = await parseVCardFile(file);
      await submitImportedContacts(rows, 'vcard_import');
    } catch (error) {
      toast.error(c.vcardFailed);
    } finally {
      setImporting(false);
      setImportMode(null);
    }
  };

  const handleInvite = async (contact) => {
    if (!inviteMessage.trim()) {
      toast.error(c.noInviteLink);
      return;
    }

    setInvitingId(contact.id);
    try {
      if (isMobileDevice()) {
        openSingleContactSms(contact.phone, inviteMessage);
      } else {
        const smsLine = `${contact.phone}\n\n${inviteMessage}`;
        await copyTextToClipboard(smsLine);
        toast.success(c.desktopCopied);
      }

      await axios.post(`/api/client/contacts/${contact.id}/invite`, {}, { withCredentials: true });
      setContacts((prev) =>
        prev.map((row) =>
          row.id === contact.id
            ? {
                ...row,
                invited_at: new Date().toISOString(),
                invite_count: (row.invite_count || 0) + 1,
              }
            : row
        )
      );
      toast.success(c.inviteOpened);
    } catch (error) {
      if (error?.message === 'NO_PHONE') {
        toast.error(c.invalidPhone);
        return;
      }
      toast.error(c.inviteFailed);
    } finally {
      setInvitingId(null);
    }
  };

  const handleDelete = async (contact) => {
    const confirmMsg = (c.confirmDelete || 'Remove "{name}"?').replace('{name}', contact.name);
    if (!(await confirm({
      message: confirmMsg,
      title: t.common.confirmDestructiveTitle,
      confirmLabel: t.common.delete,
    }))) return;

    setDeletingId(contact.id);
    try {
      await axios.delete(`/api/client/contacts/${contact.id}`, { withCredentials: true });
      setContacts((prev) => prev.filter((row) => row.id !== contact.id));
      toast.success(c.deletedSuccess);
    } catch (error) {
      toast.error(error.response?.data?.error || c.deleteFailed);
    } finally {
      setDeletingId(null);
    }
  };

  const importActions = (
    <div className="flex flex-col items-stretch gap-2 sm:items-end">
      <Button
        onClick={handleGoogleImport}
        disabled={importing || googleConfigured === false}
        className="inline-flex items-center justify-center gap-2"
      >
        <GoogleIcon />
        {importing && importMode === 'google' ? c.importing : c.importFromGoogle}
      </Button>

      {importOptions.phonePicker ? (
        <Button
          variant="secondary"
          onClick={handlePhonePickerImport}
          disabled={importing}
          className="inline-flex items-center justify-center gap-2"
        >
          <DevicePhoneMobileIcon className="h-5 w-5" />
          {importing && importMode === 'phone' ? c.importingPhone : c.importFromPhone}
        </Button>
      ) : null}

      {importOptions.vCard ? (
        <>
          <input
            ref={vcardInputRef}
            type="file"
            accept=".vcf,text/vcard,text/x-vcard"
            className="hidden"
            onChange={handleVCardImport}
          />
          <Button
            variant="secondary"
            onClick={() => vcardInputRef.current?.click()}
            disabled={importing}
            className="inline-flex items-center justify-center gap-2"
          >
            <DocumentArrowUpIcon className="h-5 w-5" />
            {importing && importMode === 'vcard' ? c.importingVCard : c.importFromICloud}
          </Button>
        </>
      ) : null}
    </div>
  );

  if (loading && contacts.length === 0) {
    return <ListPageSkeleton rows={5} />;
  }

  return (
    <div className="space-y-6">
      {dialog}
      <PageHeader
        title={c.title}
        subtitle={c.subtitle}
        action={importActions}
      />

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-premium flex items-start gap-3 border border-emerald-200/70 bg-emerald-50/60 p-4  "
      >
        <ShieldCheckIcon className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600 " />
        <div>
          <p className="text-sm font-semibold text-emerald-900 ">{c.secureTitle}</p>
          <p className="mt-1 text-sm text-emerald-800/90 ">{c.secureDesc}</p>
        </div>
      </motion.div>

      {googleConfigured === false ? (
        <p className="rounded-xl border border-amber-200/80 bg-amber-50/80 px-4 py-3 text-sm text-amber-900   ">
          {c.googleNotConfigured}
        </p>
      ) : (
        <div className="space-y-2">
          <p className="rounded-xl border border-sky-200/80 bg-sky-50/80 px-4 py-3 text-sm text-sky-900   ">
            {platformHint}
          </p>
          {importOptions.platform === 'ios' ? (
            <p className="rounded-xl border border-violet-200/80 bg-violet-50/80 px-4 py-3 text-xs text-violet-900   ">
              {c.iosVCardSteps}
            </p>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="card-premium p-4">
          <p className="stat-label">{c.totalContacts}</p>
          <p className="mt-1 text-2xl font-bold text-secondary-900 ">{contacts.length}</p>
        </div>
        <div className="card-premium p-4">
          <p className="stat-label">{c.invitedContacts}</p>
          <p className="mt-1 text-2xl font-bold text-secondary-900 ">{invitedCount}</p>
        </div>
        <div className="card-premium p-4">
          <p className="stat-label">{c.pendingInvites}</p>
          <p className="mt-1 text-2xl font-bold text-secondary-900 ">
            {Math.max(contacts.length - invitedCount, 0)}
          </p>
        </div>
      </div>

      <TableToolbar
        className="min-w-0"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={c.searchPlaceholder}
      />

      {loadError ? <ViewState error={loadError} onRetry={() => fetchContacts(search)} /> : null}

      {!loadError && contacts.length === 0 ? (
        <EmptyState
          icon={UserGroupIcon}
          title={c.emptyTitle}
          description={c.emptyDesc}
          action={importActions}
        />
      ) : !loadError ? (
        <div className="card-premium min-w-0 overflow-hidden">
          <div className="hidden overflow-x-auto lg:block">
        <Table wrap={false}>
          <TableHead>
            <tr>
              <TableHeaderCell>{c.columnName}</TableHeaderCell>
              <TableHeaderCell className="whitespace-nowrap">{c.columnPhone}</TableHeaderCell>
              <TableHeaderCell className="hidden sm:table-cell">{c.columnEmail}</TableHeaderCell>
              <TableHeaderCell className="hidden md:table-cell">{c.columnStatus}</TableHeaderCell>
              <TableHeaderCell align="right" className="whitespace-nowrap">{c.columnActions}</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell primary className="min-w-0">
                  <div className="font-medium text-secondary-900 ">{contact.name}</div>
                  {contact.invited_at ? (
                    <span className="mt-1 inline-flex rounded-full bg-success-100 px-2 py-0.5 text-2xs font-medium text-success-800   md:hidden">
                      {c.statusInvited}
                    </span>
                  ) : (
                    <span className="mt-1 inline-flex rounded-full bg-secondary-100 px-2 py-0.5 text-2xs font-medium text-secondary-600   md:hidden">
                      {c.statusPending}
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs sm:text-sm">
                  <CopyablePhone value={contact.phone} />
                </TableCell>
                <TableCell className="hidden sm:table-cell">{contact.email || '—'}</TableCell>
                <TableCell className="hidden md:table-cell">
                  {contact.invited_at ? (
                    <span className="inline-flex rounded-full bg-success-100 px-2.5 py-0.5 text-xs font-medium text-success-800  ">
                      {c.statusInvited}
                    </span>
                  ) : (
                    <span className="inline-flex rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-600  ">
                      {c.statusPending}
                    </span>
                  )}
                </TableCell>
                <TableCell align="right">
                  <div className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => handleInvite(contact)}
                      disabled={invitingId === contact.id}
                      className="inline-flex w-full items-center justify-center gap-1.5 sm:w-auto"
                    >
                      <ChatBubbleLeftEllipsisIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate text-xs sm:text-sm">
                        {invitingId === contact.id ? c.inviting : c.inviteSms}
                      </span>
                    </Button>
                    <button
                      type="button"
                      onClick={() => handleDelete(contact)}
                      disabled={deletingId === contact.id}
                      className="touch-target inline-flex items-center justify-center self-end rounded-lg text-secondary-400 transition hover:bg-error-50 hover:text-error-600 disabled:opacity-50  sm:self-auto"
                      title={c.removeContact}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
          </div>

          <div className="divide-y divide-secondary-100  lg:hidden">
            {contacts.map((contact) => (
              <div key={contact.id} className="space-y-3 p-4">
                <div className="min-w-0">
                  <div className="font-semibold text-secondary-900 ">{contact.name}</div>
                  <div className="mt-1">
                    <CopyablePhone value={contact.phone} />
                  </div>
                  {contact.email ? (
                    <p className="mt-1 truncate text-sm text-secondary-500 ">{contact.email}</p>
                  ) : null}
                </div>
                <div>
                  {contact.invited_at ? (
                    <span className="inline-flex rounded-full bg-success-100 px-2.5 py-0.5 text-xs font-medium text-success-800  ">
                      {c.statusInvited}
                    </span>
                  ) : (
                    <span className="inline-flex rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-600  ">
                      {c.statusPending}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => handleInvite(contact)}
                    disabled={invitingId === contact.id}
                    className="inline-flex w-full items-center justify-center gap-1.5"
                  >
                    <ChatBubbleLeftEllipsisIcon className="h-4 w-4 shrink-0" />
                    {invitingId === contact.id ? c.inviting : c.inviteSms}
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => handleDelete(contact)}
                    disabled={deletingId === contact.id}
                    className="inline-flex w-full items-center justify-center gap-1.5 text-error-600 hover:bg-error-50  "
                  >
                    <TrashIcon className="h-4 w-4" />
                    {c.removeContact}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ClientContactsView;
